import { Head, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import { PageProps } from '@/types'
import { DateRange } from '@/components/DateRangeFilter'
import DataTable, { Column } from '@/components/DataTable'
import ReportControlBar, { SelectFilter, makeSortHandler } from '@/components/ReportControlBar'
import { ChartCard, SimpleBarChart } from '@/components/ReportCharts'

const GROUP_BY_LABELS: Record<string, string> = {
  agent: 'Agent',
  carrier: 'Carrier',
  commission_type: 'Type',
  status: 'Status'
}

interface Row {
  group_key: string | number
  group_label: string
  count: number
  gross_amount: number
  agent_amount: number
  override_amount: number
}

interface Props extends PageProps {
  rows: Row[]
  group_by: string
  date_range: DateRange
  sort: { key: string | null; dir: string | null }
}

const PATH = '/admin/reports/commissions_summary'

function money(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export default function CommissionsSummary({ rows, group_by, date_range, sort }: Props) {
  const filterState = { group_by }
  const onSortChange = makeSortHandler(PATH, date_range, filterState)

  const columns: Column<Row>[] = [
    { key: 'group_label', header: 'Group', sortable: true },
    { key: 'count', header: 'Count', sortable: true, className: 'text-center' },
    { key: 'gross_amount', header: 'Gross $', sortable: true, render: (r) => money(r.gross_amount) },
    { key: 'agent_amount', header: 'Agent $', sortable: true, render: (r) => money(r.agent_amount) },
    { key: 'override_amount', header: 'Override $', sortable: true, render: (r) => money(r.override_amount) }
  ]

  function setGroupBy(v: string) {
    router.get(PATH, {
      group_by: v,
      preset: date_range.preset,
      ...(date_range.preset === 'custom' ? { start_date: date_range.start_date, end_date: date_range.end_date } : {})
    }, { preserveState: false })
  }

  return (
    <AppLayout>
      <Head title="Commissions Summary" />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Commissions Summary</h1>
          <p className="text-chestnut-charcoal/70 mt-1">Commission totals grouped by your chosen dimension.</p>
        </div>

        <ReportControlBar path={PATH} dateRange={date_range} filters={filterState} sortState={sort}>
          <SelectFilter
            label="Group by"
            value={group_by}
            options={[
              { value: 'agent', label: 'Agent' },
              { value: 'carrier', label: 'Carrier' },
              { value: 'commission_type', label: 'Type' },
              { value: 'status', label: 'Status' }
            ]}
            onChange={setGroupBy}
            allLabel="Agent"
          />
        </ReportControlBar>

        <ChartCard
          title={`Top 10 ${GROUP_BY_LABELS[group_by] ?? 'Groups'} by gross`}
          subtitle="Reflects the selected group and date range. Sorted by gross, descending."
          empty={rows.length === 0}
          emptyMessage="No commissions in this date range."
        >
          <SimpleBarChart
            data={[...rows]
              .sort((a, b) => b.gross_amount - a.gross_amount)
              .slice(0, 10)
              .map(r => ({ label: r.group_label, value: r.gross_amount }))}
            tooltipLabel="Gross"
          />
        </ChartCard>

        <div className="bg-white rounded-xl border border-chestnut-gray p-6">
          <DataTable
            data={rows}
            columns={columns}
            searchPlaceholder="Search groups..."
            searchKeys={['group_label']}
            pageSize={20}
            emptyMessage="No commissions in this date range."
            getRowKey={(r) => `${group_by}-${r.group_key}`}
            controlledSort={{ key: sort.key ?? 'gross_amount', direction: (sort.dir as 'asc' | 'desc') ?? 'desc' }}
            onSortChange={onSortChange}
          />
        </div>
      </div>
    </AppLayout>
  )
}
